import { useCallback, useEffect, useState } from 'react';
import {
  Page,
  Header,
  Content,
  Table,
  TableColumn,
  Progress,
} from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@material-ui/core';

// Lista/cria/edita/remove times - lê e grava direto no teams.yaml do bucket
// MinIO via o backend (packages/backend/src/modules/teamsPlugin.ts), que faz
// o read-modify-write real. Sem isso, a única forma de mexer num time era
// editando o arquivo manualmente no MinIO (link de "editar" do Catalog nativo
// do Backstage aponta pro endpoint interno do MinIO, host do cluster que o
// navegador não alcança).
const TEAM_NAME_HELP =
  'Identificador único, letras minúsculas/números/hífen (ex: plataforma, time-pagamentos)';

type TeamRow = {
  name: string;
  displayName: string;
};

export const TeamsPage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [teams, setTeams] = useState<TeamRow[] | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [savingName, setSavingName] = useState<string | undefined>();

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDisplayName, setCreateDisplayName] = useState('');

  const [editing, setEditing] = useState<TeamRow | undefined>();
  const [editDisplayName, setEditDisplayName] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<TeamRow | undefined>();

  const loadTeams = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('teams');
      const resp = await fetchApi.fetch(`${baseUrl}/teams`);
      if (resp.status === 403) {
        throw new Error('Só quem está no grupo "admin" pode ver esta lista.');
      }
      if (!resp.ok) {
        throw new Error(`Falha ao carregar times (${resp.status})`);
      }
      const data = (await resp.json()) as { teams: TeamRow[] };
      setTeams(data.teams);
      setErrorMessage(undefined);
    } catch (e) {
      setErrorMessage((e as Error).message);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const handleCreate = async () => {
    setSavingName(createName);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('teams');
      const resp = await fetchApi.fetch(`${baseUrl}/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: createName, displayName: createDisplayName }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}) as { error?: string });
        throw new Error(body.error ?? `Falha ao criar time (${resp.status})`);
      }
      setCreateOpen(false);
      setCreateName('');
      setCreateDisplayName('');
      await loadTeams();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setSavingName(undefined);
    }
  };

  const handleEdit = async () => {
    if (!editing) return;
    setSavingName(editing.name);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('teams');
      const resp = await fetchApi.fetch(`${baseUrl}/teams/${editing.name}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName: editDisplayName }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}) as { error?: string });
        throw new Error(body.error ?? `Falha ao editar time (${resp.status})`);
      }
      setEditing(undefined);
      await loadTeams();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setSavingName(undefined);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSavingName(deleteTarget.name);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('teams');
      const resp = await fetchApi.fetch(`${baseUrl}/teams/${deleteTarget.name}`, {
        method: 'DELETE',
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}) as { error?: string });
        throw new Error(body.error ?? `Falha ao remover time (${resp.status})`);
      }
      setDeleteTarget(undefined);
      await loadTeams();
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setSavingName(undefined);
    }
  };

  const columns: TableColumn<TeamRow>[] = [
    { title: 'Identificador', field: 'name' },
    { title: 'Nome de exibição', field: 'displayName' },
    {
      title: 'Ações',
      field: 'name',
      sorting: false,
      render: row => (
        <>
          <Button
            size="small"
            variant="outlined"
            disabled={savingName === row.name}
            onClick={() => {
              setEditing(row);
              setEditDisplayName(row.displayName);
            }}
          >
            Editar
          </Button>{' '}
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            disabled={savingName === row.name}
            onClick={() => setDeleteTarget(row)}
          >
            Remover
          </Button>
        </>
      ),
    },
  ];

  return (
    <Page themeId="tool">
      <Header title="Times" subtitle="Donos de Components/APIs no catálogo (bucket MinIO)" />
      <Content>
        {errorMessage && <p>{errorMessage}</p>}
        {!teams && !errorMessage && <Progress />}
        <Button variant="contained" color="primary" onClick={() => setCreateOpen(true)}>
          Criar time
        </Button>
        {teams && (
          <Table
            title={`${teams.length} time(s)`}
            columns={columns}
            data={teams}
            options={{ search: true, paging: teams.length > 20 }}
          />
        )}
      </Content>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Criar time</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Identificador"
            helperText={TEAM_NAME_HELP}
            value={createName}
            onChange={e => setCreateName(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Nome de exibição"
            value={createDisplayName}
            onChange={e => setCreateDisplayName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button
            color="primary"
            disabled={!createName || !createDisplayName || savingName === createName}
            onClick={handleCreate}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editing} onClose={() => setEditing(undefined)}>
        <DialogTitle>Editar time</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Identificador"
            value={editing?.name ?? ''}
            disabled
          />
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Nome de exibição"
            value={editDisplayName}
            onChange={e => setEditDisplayName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(undefined)}>Cancelar</Button>
          <Button
            color="primary"
            disabled={!editDisplayName || savingName === editing?.name}
            onClick={handleEdit}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(undefined)}>
        <DialogTitle>Remover time</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remover <strong>{deleteTarget?.displayName}</strong> ({deleteTarget?.name})?
            Isso não afeta contas/login - só tira o time do catálogo.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(undefined)}>Cancelar</Button>
          <Button
            color="secondary"
            disabled={savingName === deleteTarget?.name}
            onClick={handleDelete}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};
